import { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { MiddlewareAPI } from "redux";

import { getApiPath } from "../../components/utilities/ApiUtils";
import { parseResponseBody } from "../../utils/httpResponse";
import { createFetchInstance, type QueryAwareRequestInit } from "../fetch";
import type { ReduxActionMeta } from "../types/ActionMeta";

type BaseQueryEndpoint =
    { path: string; url?: never } | { url: string; path?: never };

export type IBaseQueryArgs = BaseQueryEndpoint & {
    method?: RequestInit["method"];
    body?: unknown;
    headers?: HeadersInit;
    queryRefreshMeta?: ReduxActionMeta;
};

export type IBaseQueryError = {
    status: number | string;
    data: unknown;
};

/**
 * Shared RTK Query baseQuery that reuses the app's existing fetch pipeline.
 * This preserves csrf-token header injection, cookies, and API telemetry events.
 */
export const baseQuery: BaseQueryFn<
    IBaseQueryArgs,
    unknown,
    IBaseQueryError
> = async (args, api) => {
    try {
        const fetchInstance = createFetchInstance({
            dispatch: api.dispatch,
            getState: api.getState,
        } as MiddlewareAPI);

        const init: QueryAwareRequestInit = {
            method: args.method ?? "POST",
            credentials: "same-origin",
            signal: api.signal,
            queryRefreshMeta: args.queryRefreshMeta,
            headers: {
                ...(args.body != null
                    ? { "Content-Type": "application/json" }
                    : {}),
                ...args.headers,
            },
        };

        if (args.body != null) {
            init.body =
                typeof args.body === "string" || args.body instanceof FormData
                    ? args.body
                    : JSON.stringify(args.body);
        }

        const hasPath = typeof args.path === "string" && args.path.length > 0;
        const hasUrl = typeof args.url === "string" && args.url.length > 0;
        if (hasPath === hasUrl) {
            throw new Error(
                "RTK baseQuery requires exactly one non-empty path or url"
            );
        }

        const endpoint = hasUrl ? args.url! : getApiPath(args.path!);
        const response = await fetchInstance.fetch(endpoint, init);
        const data = await parseResponseBody(response);

        if (!response.ok) {
            return {
                error: {
                    status: response.status,
                    data,
                },
            };
        }

        return { data };
    } catch (error) {
        return {
            error: {
                status: "FETCH_ERROR",
                data: error instanceof Error ? error.message : error,
            },
        };
    }
};
